import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SystemConfigurationItem } from 'common-models';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
    useDeleteSystemConfigurationMutation,
    useGetAllSystemConfigurationsQuery,
    useSetSystemConfigurationMutation
} from '../../../features/Admin/systemConfigApiSlice';

interface ConfigurationDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (key: string, config: SystemConfigurationItem) => void;
    editingConfig?: { key: string; config: SystemConfigurationItem };
}

const ConfigurationDialog: FC<ConfigurationDialogProps> = ({ open, onClose, onSave, editingConfig }) => {
    const [key, setKey] = useState('');
    const [value, setValue] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (editingConfig) {
            setKey(editingConfig.key);
            setValue(editingConfig.config.value);
            setDescription(editingConfig.config.description || '');
        } else {
            setKey('');
            setValue('');
            setDescription('');
        }
    }, [editingConfig, open]);

    const handleSave = () => {
        if (!key.trim() || !value.trim()) {
            return;
        }

        const config: SystemConfigurationItem = {
            key: key.trim(),
            value: value.trim(),
            description: description.trim() || undefined,
            updatedAt: new Date(),
        };

        onSave(key.trim(), config);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {editingConfig ? 'Edit Configuration' : 'Add New Configuration'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Configuration Key"
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        disabled={!!editingConfig}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Value"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        multiline
                        rows={3}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!key.trim() || !value.trim()}
                    startIcon={<SaveIcon />}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SystemConfigAdminPage: FC = () => {
    const auth = useAuth();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState<{ key: string; config: SystemConfigurationItem } | undefined>();
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const { data: configurations = {}, isLoading, error } = useGetAllSystemConfigurationsQuery(auth.user?.access_token ?? '');
    const [setConfiguration] = useSetSystemConfigurationMutation();
    const [deleteConfiguration] = useDeleteSystemConfigurationMutation();

    const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSaveConfiguration = async (key: string, config: SystemConfigurationItem) => {
        try {
            await setConfiguration({ key, config, accessToken: auth.user?.access_token ?? '' }).unwrap();
            showSnackbar(`Configuration "${key}" saved successfully`);
        } catch (error) {
            console.error('Failed to save configuration:', error);
            showSnackbar('Failed to save configuration', 'error');
        }
    };

    const handleEditConfiguration = (key: string, config: SystemConfigurationItem) => {
        setEditingConfig({ key, config });
        setDialogOpen(true);
    };

    const handleDeleteConfiguration = async (key: string) => {
        if (!window.confirm(`Are you sure you want to delete the configuration "${key}"?`)) {
            return;
        }

        try {
            await deleteConfiguration({ key, accessToken: auth.user?.access_token ?? '' }).unwrap();
            showSnackbar(`Configuration "${key}" deleted successfully`);
        } catch (error) {
            console.error('Failed to delete configuration:', error);
            showSnackbar('Failed to delete configuration', 'error');
        }
    };

    const handleAddConfiguration = () => {
        setEditingConfig(undefined);
        setDialogOpen(true);
    };

    const configKeys = Object.keys(configurations).sort();

    if (isLoading) {
        return (
            <Root>
                <Typography variant="h4" gutterBottom>
                    System Configuration
                </Typography>
                <Typography>Loading configurations...</Typography>
            </Root>
        );
    }

    if (error) {
        return (
            <Root>
                <Typography variant="h4" gutterBottom>
                    System Configuration
                </Typography>
                <Alert severity="error">
                    Failed to load system configurations. Please try again.
                </Alert>
            </Root>
        );
    }

    return (
        <Root>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4">
                    System Configuration
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddConfiguration}
                >
                    Add Configuration
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Key</TableCell>
                            <TableCell>Value</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Updated</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {configKeys.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No configurations found. Click "Add Configuration" to create one.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            configKeys.map((key) => {
                                const config = configurations[key];
                                return (
                                    <TableRow key={key} hover>
                                        <TableCell>
                                            <Chip
                                                label={key}
                                                variant="outlined"
                                                size="small"
                                                className={classes.keyChip}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                                                {config.value}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                                                {config.description || '—'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {config.updatedAt ? new Date(config.updatedAt).toLocaleDateString() : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditConfiguration(key, config)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteConfiguration(key)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ConfigurationDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSaveConfiguration}
                editingConfig={editingConfig}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Root>
    );
};

const PREFIX = 'SystemConfigAdminPage';
const classes = {
    keyChip: `${PREFIX}-keyChip`,
};

const Root = styled('div')(({ theme }) => ({
    padding: theme.spacing(3),
    [`& .${classes.keyChip}`]: {
        fontFamily: 'monospace',
        fontWeight: 'bold',
    },
}));

export default SystemConfigAdminPage;
